import React, { useState, useCallback } from "react";
import ActivitySidebar from "./components/ActivitySidebar";
import ActivityEditor from "./components/ActivityEditor";
import { useActivities } from "./hooks/useActivities";
import { activityDefaults } from "../../../models/activity";
import { ConfirmDialog, MasterDetailLayout, Breadcrumb } from "../../../components/Common";

/**
 * Activities Page (Módulo de Atividades e Malha Pedagógica)
 * Layout Master-Detail: Listagem lateral e editor completo de malha.
 */
const ActivitiesPage = () => {
    const { activities, loading, isSaving, hasNeoMethodology, planId, actions } = useActivities();
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [isDirty, setIsDirty] = useState(false);

    // Seleção inicial automática
    React.useEffect(() => {
        if (!selectedActivity && activities.length > 0 && !loading) {
            setSelectedActivity(activities[0]);
        }
    }, [activities, loading, selectedActivity]);

    const handleSelectActivity = useCallback(async (activity) => {
        if (selectedActivity?.id === activity.id) return;

        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Você tem alterações pendentes nesta atividade. Deseja descartá-las?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }

        setSelectedActivity(activity);
        setIsDirty(false);
    }, [selectedActivity, isDirty]);

    const handleCreateNew = useCallback(async () => {
        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Deseja descartar as mudanças atuais e criar uma nova atividade?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }
        setSelectedActivity(activityDefaults);
        setIsDirty(false);
    }, [isDirty]);

    const handleSave = useCallback(async (data) => {
        const success = await actions.saveActivity(data);
        if (success) {
            setIsDirty(false);
        }
    }, [actions]);

    const handleDelete = useCallback(async (id) => {
        const success = await actions.deleteActivity(id);
        if (success) {
            setSelectedActivity(null);
            setIsDirty(false);
        }
    }, [actions]);

    const handleMarkDirty = useCallback((dirty) => setIsDirty(dirty), []);

    // PageLoader logic removido para carregamento nativo do sidebar/detalhe

    const hasAccessToMethodology = hasNeoMethodology === true || planId === "neo";

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Atividades e Malha Pedagógica" />
                {hasAccessToMethodology && (
                    <button 
                        onClick={actions.seedDefaults}
                        className="btn btn-warning btn-sm fw-bold d-flex align-items-center gap-1"
                        disabled={isSaving || loading}
                    >
                        <i className="mdi mdi-import"></i> Importar Metodologia NEO
                    </button>
                )}
            </div>

            <div className="border-top mb-3"></div>

            <div className="activities-container mt-3">
                <MasterDetailLayout
                master={
                    <ActivitySidebar
                        activities={activities}
                        activeId={selectedActivity?.id}
                        onSelect={handleSelectActivity}
                        onCreateNew={handleCreateNew}
                        loading={loading}
                    />
                }
                detail={
                    <ActivityEditor
                        activity={selectedActivity}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        isSaving={isSaving}
                        onChange={handleMarkDirty}
                    />
                }
            />
            </div>
        </React.Fragment>
    );
};

export default ActivitiesPage;
